import { useMemo, useState } from 'react';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import Table from '../components/ui/Table';
import PageHeader from '../components/shared/PageHeader';
import ClientSearchWithRegistration from '../components/forms/ClientSearchWithRegistration';
import VehicleManagementWithRegistration from '../components/forms/VehicleManagementWithRegistration';
import { supabase } from '../lib/supabase';
import { currency } from '../utils/formatters';
import { normalizeMoney, onlyDigits } from '../utils/masks';
import { useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  createServiceOrder,
  deleteServiceOrder,
  SERVICE_ORDER_STATUSES,
  serviceOrderStatusLabel,
} from '../lib/serviceOrders';

const tabs = ['todas', 'aberta', 'diagnostico', 'aguardando_aprovacao', 'em_execucao', 'aguardando_peca', 'finalizada', 'entregue', 'atrasada'];

export default function OrdensServico() {
  const navigate = useNavigate();
  const { perfil } = useAuth();
  const canViewFinance = perfil?.perfil === 'admin';
  const [status, setStatus] = useState('todas');
  const [busca, setBusca] = useState('');
  const [mecanicoFiltro, setMecanicoFiltro] = useState('');
  const [periodoFiltro, setPeriodoFiltro] = useState('30');
  const [rowsAll, setRowsAll] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [mecanicos, setMecanicos] = useState([]);
  const [officeId, setOfficeId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingOS, setDeletingOS] = useState(false);
  const canDeleteOS = perfil?.perfil === 'admin';

  const load = async () => {
    const { data: authData } = await supabase.auth.getUser()
    const { data: userRow } = await supabase
      .from('usuarios')
      .select('oficina_id')
      .eq('email', authData.user.email)
      .single()
    const oficina_id = userRow?.oficina_id
    
    if (!oficina_id) return;
    
    setOfficeId(oficina_id);
    const [ordensQ, clientesQ, veiculosQ, usersQ] = await Promise.all([
      supabase.from('ordens_servico').select('*').eq('oficina_id', oficina_id).order('data_entrada', { ascending: false }),
      supabase.from('clientes').select('id,nome').eq('oficina_id', oficina_id).is('removed_at', null),
      supabase.from('veiculos').select('id,modelo,placa,cliente_id').eq('oficina_id', oficina_id).is('removed_at', null),
      supabase.from('usuarios').select('id,nome').eq('oficina_id', oficina_id),
    ]);
    setRowsAll(ordensQ.data || []);
    setClients(clientesQ.data || []);
    setVehicles(veiculosQ.data || []);
    setMecanicos(usersQ.data || []);
  };

  useEffect(() => { load(); }, []);

  const handleDeleteOS = async () => {
    if (!deleteTarget?.id || !officeId || deletingOS) return;

    setDeletingOS(true);
    try {
      await deleteServiceOrder(deleteTarget.id);

      toast.success('OS excluída com sucesso');
      setRowsAll((items) => items.filter((item) => item.id !== deleteTarget.id));
      setDeleteTarget(null);
      load();
    } catch (error) {
      console.error('Erro ao excluir OS:', error);
      toast.error(error.message || 'Não foi possível excluir a OS');
    } finally {
      setDeletingOS(false);
    }
  };

  const rows = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const dias = periodoFiltro === 'all' ? null : Number(periodoFiltro);
    const limite = dias ? new Date(Date.now() - dias * 24 * 60 * 60 * 1000) : null;

    return rowsAll.filter((o) => {
      const cliente = (clients || []).find((c) => c.id === o.cliente_id);
      const veiculo = (vehicles || []).find((v) => v.id === o.veiculo_id);
      const texto = [
        o.numero,
        cliente?.nome,
        veiculo?.modelo,
        veiculo?.placa,
      ].filter(Boolean).join(' ').toLowerCase();
      const dataEntrada = o.data_entrada ? new Date(o.data_entrada) : null;

      return (
        (status === 'todas' || o.status === status) &&
        (!mecanicoFiltro || o.mecanico_id === mecanicoFiltro) &&
        (!limite || (dataEntrada && dataEntrada >= limite)) &&
        (!termo || texto.includes(termo))
      );
    });
  }, [status, busca, mecanicoFiltro, periodoFiltro, rowsAll, clients, vehicles]);
  const cols = ['Número', 'Cliente', 'Veículo', 'Mecânico', 'Status', ...(canViewFinance ? ['Valor'] : []), 'Ações'];
  return (
    <div>
      <PageHeader title="Ordens de Serviço" subtitle="Gestão completa do fluxo da oficina" actions={<Button onClick={() => navigate('/ordens/nova')}><Plus size={15} />Nova OS</Button>} />
      <div className="mb-4 grid gap-2 md:grid-cols-4">
        <Input placeholder="Buscar por cliente, veículo ou número da OS" value={busca} onChange={(e) => setBusca(e.target.value)} />
        <Select value={mecanicoFiltro} onChange={(e) => setMecanicoFiltro(e.target.value)} options={[{ value: '', label: 'Todos mecânicos' }, ...(mecanicos || []).map((m) => ({ value: m.id, label: m.nome }))]} />
        <Select value={periodoFiltro} onChange={(e) => setPeriodoFiltro(e.target.value)} options={[{ value: '30', label: 'Período: últimos 30 dias' }, { value: '7', label: 'Últimos 7 dias' }, { value: 'all', label: 'Todos os períodos' }]} />
        <Select value={status === 'todas' ? '' : status} onChange={(e) => setStatus(e.target.value || 'todas')} options={[{ value: '', label: 'Todos os status' }, ...SERVICE_ORDER_STATUSES]} />
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        {tabs.map((t) => <button key={t} onClick={() => setStatus(t)} className={`rounded-lg px-3 py-1.5 text-sm ${status === t ? 'bg-accent/20 text-accent' : 'bg-surface2 text-zinc-300'}`}>{t === 'todas' ? 'Todas' : serviceOrderStatusLabel(t)}</button>)}
      </div>
      <Table
        columns={cols}
        rows={rows}
        renderRow={(o) => (
          <tr key={o.id} className="border-t border-border hover:bg-surface2">
            <td className="px-3 py-2">{o.numero}</td>
            <td className="px-3 py-2">{(clients || []).find((c) => c.id === o.cliente_id)?.nome}</td>
            <td className="px-3 py-2">{(vehicles || []).find((v) => v.id === o.veiculo_id)?.modelo}</td>
            <td className="px-3 py-2">{(mecanicos || []).find((m) => m.id === o.mecanico_id)?.nome}</td>
            <td className="px-3 py-2"><Badge>{serviceOrderStatusLabel(o.status)}</Badge></td>
            {canViewFinance && <td className="px-3 py-2">{currency(o.valor_total)}</td>}
            <td className="px-3 py-2">
              <div className="flex gap-2">
                <Link to={`/ordens/${o.id}`} className="rounded-md bg-surface2 p-1.5 hover:bg-zinc-800"><Eye size={14} /></Link>
                <button type="button" onClick={() => navigate(`/ordens/${o.id}/edit`)} className="rounded-md bg-surface2 p-1.5 hover:bg-zinc-800"><Pencil size={14} /></button>
                {canDeleteOS && (
                  <button type="button" onClick={() => setDeleteTarget(o)} className="rounded-md bg-red-600/15 p-1.5 text-red-300 hover:bg-red-600/25" title="Excluir OS">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </td>
          </tr>
        )}
      />
      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Excluir OS">
        <div className="space-y-4">
          <p className="text-zinc-300">
            Confirma a exclusão definitiva da OS <strong>{deleteTarget?.numero}</strong>?
          </p>
          <p className="text-sm text-zinc-500">
            Serviços, peças e lançamento financeiro vinculados serão removidos. Peças baixadas do estoque serão devolvidas.
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deletingOS}>Cancelar</Button>
            <Button variant="danger" onClick={handleDeleteOS} disabled={deletingOS}>
              {deletingOS ? 'Excluindo...' : 'Excluir OS'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function FormOS({ mecanicos, officeId, canViewFinance, onCancel, onCreated }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Client and Vehicle states
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  
  // Service details
  const [problema, setProblema] = useState('');
  const [kmAtual, setKmAtual] = useState('');
  const [servicos, setServicos] = useState([{ servicoId: '', nome: '', valor: 0, custo: 0 }]);
  const [pecas, setPecas] = useState([{ itemId: '', nome: '', qtd: 1, valorUnitario: '' }]);
  
  // Final details
  const [mecanicoId, setMecanicoId] = useState('');
  const [previsaoEntrega, setPrevisaoEntrega] = useState('');
  const [lembreteContatoEm, setLembreteContatoEm] = useState('');
  const [lembreteContatoObs, setLembreteContatoObs] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('');
  const [observacoes, setObservacoes] = useState('');

  const [itemsEstoque, setItemsEstoque] = useState([]);
  const [servicosCatalog, setServicosCatalog] = useState([]);
  
  useEffect(() => {
    if (officeId) {
      supabase.from('estoque').select('id,nome,preco_venda,preco_custo,quantidade').eq('oficina_id', officeId).then(({ data }) => setItemsEstoque(data || []));
      supabase.from('servicos_oficina').select('id,nome,preco,custo,descricao_padrao,duracao_estimada').eq('oficina_id', officeId).eq('ativo', true).order('nome').then(({ data }) => setServicosCatalog(data || []));
    }
  }, [officeId]);

  const totalServicos = (servicos || []).reduce((acc, s) => acc + normalizeMoney(s.valor), 0);
  const totalPecas = (pecas || []).reduce((acc, p) => {
    return acc + Number(p.valorUnitario || 0) * Number(p.qtd || 0);
  }, 0);
  const totalGeral = totalServicos + totalPecas;
  const totalCustos = (servicos || []).reduce((acc, s) => acc + normalizeMoney(s.custo), 0)
    + (pecas || []).reduce((acc, p) => {
      const item = (itemsEstoque || []).find((i) => String(i.id) === String(p.itemId));
      return acc + Number(item?.preco_custo || 0) * Number(p.qtd || 0);
    }, 0);

  const formasPagamento = [
    { value: '', label: 'Selecione...' },
    { value: 'dinheiro', label: 'Dinheiro' },
    { value: 'cartao_credito', label: 'Cartão de Crédito' },
    { value: 'cartao_debito', label: 'Cartão de Débito' },
    { value: 'pix', label: 'PIX' },
    { value: 'boleto', label: 'Boleto' },
    { value: 'misto', label: 'Misto' }
  ];

  const submit = async () => {
    if (loading) return;
    if (!selectedClient || !selectedVehicle) {
      toast.error('Selecione cliente e veículo');
      return;
    }

    if (!problema.trim()) {
      toast.error('Descreva o problema relatado pelo cliente');
      return;
    }

    const insufficientItem = (pecas || []).find((p) => {
      if (!p.itemId) return false;
      const item = (itemsEstoque || []).find((stockItem) => String(stockItem.id) === String(p.itemId));
      return Number(p.qtd || 1) > Number(item?.quantidade || 0);
    });

    if (insufficientItem) {
      const item = (itemsEstoque || []).find((stockItem) => String(stockItem.id) === String(insufficientItem.itemId));
      toast.error(`Estoque insuficiente para ${item?.nome || 'a peca selecionada'}.`);
      return;
    }

    setLoading(true);
    try {
      await createServiceOrder({
        cliente_id: selectedClient.id,
        veiculo_id: selectedVehicle.id,
        mecanico_id: mecanicoId || null,
        problema_relatorio: problema,
        km_entrada: kmAtual || null,
        data_previsao: previsaoEntrega || null,
        lembrete_contato_em: lembreteContatoEm || null,
        lembrete_contato_obs: lembreteContatoObs.trim() || null,
        forma_pagamento: formaPagamento || null,
        observacoes: observacoes || null,
        servicos: (servicos || [])
          .filter((service) => service.nome.trim())
          .map((service) => ({
            servico_id: service.servicoId || null,
            descricao: service.nome.trim(),
            valor: canViewFinance ? normalizeMoney(service.valor) : 0,
            custo: canViewFinance ? normalizeMoney(service.custo) : 0,
          })),
        pecas: (pecas || [])
          .filter((part) => part.itemId || part.nome?.trim())
          .map((part) => {
            const stockItem = (itemsEstoque || []).find((item) => String(item.id) === String(part.itemId));
            return {
              estoque_id: stockItem?.id || null,
              nome: stockItem?.nome || part.nome.trim(),
              quantidade: Number(part.qtd || 1),
              valor_unitario: canViewFinance
                ? Number(stockItem?.preco_venda || normalizeMoney(part.valorUnitario) || 0)
                : 0,
            };
          }),
      });
      
      toast.success('OS criada com sucesso!');
      onCreated();
    } catch (error) {
      console.error('Erro ao criar OS:', error);
      toast.error(error.message || 'Não foi possível criar a OS. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !selectedClient) {
      toast.error('Selecione um cliente');
      return;
    }
    if (currentStep === 2 && !selectedVehicle) {
      toast.error('Selecione um veículo');
      return;
    }
    if (currentStep === 3 && !problema.trim()) {
      toast.error('Descreva o problema');
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Step 1: Client Selection
  if (currentStep === 1) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Etapa 1 — Cliente</h3>
          <p className="text-zinc-400 text-sm mb-4">
            Busque um cliente existente ou cadastre um novo
          </p>
        </div>
        
        <ClientSearchWithRegistration 
          officeId={officeId}
          onClientSelected={setSelectedClient}
          selectedClient={selectedClient}
        />

        <div className="flex justify-between pt-4">
          <Button variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
          <Button onClick={nextStep} disabled={!selectedClient}>
            Próximo → Veículo
          </Button>
        </div>
      </div>
    );
  }

  // Step 2: Vehicle Selection
  if (currentStep === 2) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Etapa 2 — Veículo</h3>
          <p className="text-zinc-400 text-sm mb-4">
            Selecione um veículo do cliente ou cadastre um novo
          </p>
        </div>
        
        <VehicleManagementWithRegistration 
          officeId={officeId}
          clientId={selectedClient?.id}
          onVehicleSelected={setSelectedVehicle}
          selectedVehicle={selectedVehicle}
        />

        <div className="flex justify-between pt-4">
          <Button variant="ghost" onClick={prevStep}>
            ← Cliente
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Sair
          </Button>
          <Button onClick={nextStep} disabled={!selectedVehicle}>
            Próximo → Serviço
          </Button>
        </div>
      </div>
    );
  }

  // Step 3: Service Details
  if (currentStep === 3) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Etapa 3 — Serviço</h3>
          <p className="text-zinc-400 text-sm mb-4">
            Descreva o problema e adicione serviços e peças necessárias
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">Problema relatado pelo cliente *</label>
          <textarea
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none"
            rows={3}
            placeholder="Descreva o problema ou o que o cliente relatou..."
            value={problema}
            onChange={(e) => setProblema(e.target.value)}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">KM atual do veículo</label>
          <Input
            placeholder="Ex: 45000"
            mask="integer"
            value={kmAtual}
            onChange={(e) => setKmAtual(onlyDigits(e.target.value, 7))}
          />
        </div>

        <div className="rounded-lg border border-border p-4">
          <h4 className="font-medium mb-3">Serviços a realizar</h4>
          <div className="space-y-2">
            {(servicos || []).map((s, idx) => (
              <div key={`s-${idx}`} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_130px_130px_44px]">
                <select
                  value={s.servicoId}
                  onChange={(e) => setServicos((prev) =>
                    (prev || []).map((x, i) => {
                      if (i !== idx) return x;
                      const item = (servicosCatalog || []).find((service) => String(service.id) === String(e.target.value));
                      return {
                        ...x,
                        servicoId: e.target.value,
                        nome: item?.descricao_padrao || item?.nome || x.nome,
                        valor: canViewFinance ? Number(item?.preco || 0) : 0,
                        custo: canViewFinance ? Number(item?.custo || 0) : 0,
                      };
                    })
                  )}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none"
                >
                  <option value="">Serviço cadastrado</option>
                  {(servicosCatalog || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.nome}{canViewFinance ? ` (${currency(item.preco)})` : ''}
                    </option>
                  ))}
                </select>
                <Input
                  placeholder="Nome do serviço"
                  value={s.nome}
                  onChange={(e) => setServicos((prev) => 
                    (prev || []).map((x, i) => 
                      i === idx ? { ...x, servicoId: '', nome: e.target.value } : x
                    )
                  )}
                />
                {canViewFinance && (
                  <>
                    <Input
                      placeholder="Venda"
                      mask="money"
                      value={s.valor}
                      onChange={(e) => setServicos((prev) => 
                        (prev || []).map((x, i) => 
                          i === idx ? { ...x, valor: e.target.value } : x
                        )
                      )}
                    />
                    <Input
                      placeholder="Custo"
                      mask="money"
                      value={s.custo}
                      onChange={(e) => setServicos((prev) => 
                        (prev || []).map((x, i) => 
                          i === idx ? { ...x, custo: e.target.value } : x
                        )
                      )}
                    />
                  </>
                )}
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => setServicos((prev) => {
                    const next = (prev || []).filter((_, i) => i !== idx);
                    return next.length ? next : [{ servicoId: '', nome: '', valor: 0, custo: 0 }];
                  })}
                  className="justify-center gap-2 sm:px-0"
                >
                  <Trash2 size={16} />
                  <span className="sm:hidden">Remover serviço</span>
                </Button>
              </div>
            ))}
            <Button 
              variant="ghost" 
              type="button" 
              onClick={() => setServicos((prev) => [...prev, { servicoId: '', nome: '', valor: 0, custo: 0 }])}
              className="w-full"
            >
              + Adicionar serviço
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-border p-4">
          <h4 className="font-medium mb-3">Peças necessárias</h4>
          <div className="space-y-2">
            {(pecas || []).map((p, idx) => (
              <div key={`p-${idx}`} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_90px_130px_44px]">
                <select
                  value={p.itemId}
                  onChange={(e) => setPecas((prev) => 
                    (prev || []).map((x, i) => {
                      if (i === idx) {
                        const item = (itemsEstoque || []).find((it) => String(it.id) === String(e.target.value));
                        return { 
                          ...x, 
                          itemId: e.target.value, 
                          nome: item?.nome || x.nome,
                          valorUnitario: canViewFinance ? Number(item?.preco_venda || 0) : 0
                        };
                      }
                      return x;
                    })
                  )}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none"
                >
                  <option value="">Selecione peça</option>
                  {(itemsEstoque || []).map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.nome}{canViewFinance ? ` (${currency(i.preco_venda)})` : ''} - Estoque: {i.quantidade}
                    </option>
                  ))}
                </select>
                <Input
                  placeholder="Nome da peça"
                  value={p.nome}
                  onChange={(e) => setPecas((prev) => 
                    (prev || []).map((x, i) => 
                      i === idx ? { ...x, itemId: '', nome: e.target.value } : x
                    )
                  )}
                />
                <Input
                  placeholder="Ex: 1"
                  mask="integer"
                  value={p.qtd}
                  onChange={(e) => setPecas((prev) => 
                    (prev || []).map((x, i) => 
                      i === idx ? { ...x, qtd: Number(onlyDigits(e.target.value, 4) || 0) } : x
                    )
                  )}
                />
                {canViewFinance && (
                  <Input
                    placeholder="Valor unitário"
                    mask="money"
                    value={p.valorUnitario}
                    onChange={(e) => setPecas((prev) => 
                      (prev || []).map((x, i) => 
                        i === idx ? { ...x, itemId: '', valorUnitario: e.target.value } : x
                      )
                    )}
                  />
                )}
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => setPecas((prev) => {
                    const next = (prev || []).filter((_, i) => i !== idx);
                    return next.length ? next : [{ itemId: '', nome: '', qtd: 1, valorUnitario: '' }];
                  })}
                  className="justify-center gap-2 sm:px-0"
                >
                  <Trash2 size={16} />
                  <span className="sm:hidden">Remover peça</span>
                </Button>
              </div>
            ))}
            {(itemsEstoque || []).length === 0 && (
              <p className="text-xs text-zinc-400">
                Nenhuma peça cadastrada no estoque. Informe nome e valor manualmente para incluir na OS.
              </p>
            )}
            <Button 
              variant="ghost" 
              type="button" 
              onClick={() => setPecas((prev) => [...prev, { itemId: '', nome: '', qtd: 1, valorUnitario: '' }])}
              className="w-full"
            >
              + Adicionar peça
            </Button>
          </div>
        </div>

        {canViewFinance && <div className="bg-surface2 rounded-lg p-4">
          <div className="text-right">
            <div className="text-sm text-zinc-400">Total serviços: {currency(totalServicos)}</div>
            <div className="text-sm text-zinc-400">Total peças: {currency(totalPecas)}</div>
            <div className="text-sm text-zinc-400">Custo estimado: {currency(totalCustos)}</div>
            <div className="text-sm text-green-400">Lucro bruto: {currency(totalGeral - totalCustos)}</div>
            <div className="text-lg font-semibold">Total estimado: {currency(totalGeral)}</div>
          </div>
        </div>}

        <div className="flex justify-between pt-4">
          <Button variant="ghost" onClick={prevStep}>
            ← Veículo
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Sair
          </Button>
          <Button onClick={nextStep} disabled={!problema.trim()}>
            Próximo → Finalizar
          </Button>
        </div>
      </div>
    );
  }

  // Step 4: Final Details
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Etapa 4 — Finalizar</h3>
        <p className="text-zinc-400 text-sm mb-4">
          Complete os detalhes finais e crie a ordem de serviço
        </p>
      </div>

      {/* Summary */}
      <Card className="bg-surface2">
        <h4 className="font-medium mb-3">Resumo da OS</h4>
        <div className="space-y-2 text-sm">
          <div><strong>Cliente:</strong> {selectedClient?.nome}</div>
          <div><strong>Veículo:</strong> {selectedVehicle?.modelo} - {selectedVehicle?.placa}</div>
          <div><strong>Problema:</strong> {problema}</div>
          {canViewFinance && <div><strong>Total:</strong> {currency(totalGeral)}</div>}
        </div>
      </Card>

      <div>
        <label className="mb-1 block text-sm font-medium">Mecânico responsável</label>
        <select
          value={mecanicoId}
          onChange={(e) => setMecanicoId(e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none"
        >
          <option value="">Selecione um mecânico</option>
          {(mecanicos || []).map((m) => (
            <option key={m.id} value={m.id}>{m.nome}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">Previsão de entrega</label>
        <Input
          type="date"
          value={previsaoEntrega}
          onChange={(e) => setPrevisaoEntrega(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium">Lembrete de contato (opcional)</label>
          <Input
            type="date"
            placeholder="Opcional: escolha a data do lembrete"
            aria-label="Data opcional para lembrar o admin de contatar o cliente"
            title="Opcional: escolha quando o sistema deve lembrar o admin de contatar o cliente."
            value={lembreteContatoEm}
            onChange={(e) => setLembreteContatoEm(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
          />
          <p className="mt-1 text-xs text-zinc-500">Opcional. Escolha uma data para o painel lembrar o admin de entrar em contato com o cliente após revisão, entrega ou retorno combinado.</p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Observação do lembrete (opcional)</label>
          <Input
            placeholder="Opcional: ex. ligar 30 dias após a revisão para oferecer retorno"
            value={lembreteContatoObs}
            onChange={(e) => setLembreteContatoObs(e.target.value)}
          />
        </div>
      </div>

      {canViewFinance && <div>
        <label className="mb-1 block text-sm font-medium">Forma de pagamento estimada</label>
        <select
          value={formaPagamento}
          onChange={(e) => setFormaPagamento(e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none"
        >
          {formasPagamento.map(fp => (
            <option key={fp.value} value={fp.value}>{fp.label}</option>
          ))}
        </select>
      </div>}

      <div>
        <label className="mb-1 block text-sm font-medium">Observações internas</label>
        <textarea
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none"
          rows={3}
          placeholder="Observações internas sobre a OS..."
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
        />
      </div>

      {canViewFinance && <div className="bg-accent/10 rounded-lg p-4 border border-accent/20">
        <div className="text-center">
          <div className="text-sm text-zinc-400 mb-1">Valor Total</div>
          <div className="text-2xl font-bold">{currency(totalGeral)}</div>
        </div>
      </div>}

      <div className="flex justify-between pt-4">
        <Button variant="ghost" onClick={prevStep} disabled={loading}>
          ← Serviço
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={loading}>
          Sair
        </Button>
        <Button onClick={submit} disabled={loading} className="bg-green-600 hover:bg-green-700">
          {loading ? 'Criando OS...' : 'Criar OS'}
        </Button>
      </div>
    </div>
  );
}

